<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiTooltip } from "@shopexperts/shared";
import {
  calculateDeliveryDeadline,
  formatCompactDate,
  formatCurrency,
  getDeliveryWindowLabel,
} from "@/v2Projects/utils/display";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";
import type { QuoteInterval } from "@/v2Projects/types";

interface QuoteStripQuote {
  amount_cents: number;
  currency?: string | null;
  interval: QuoteInterval;
  quantity: number;
  delivery_after_days?: number | null;
  includes_weekends?: boolean | null;
  hours_bank_expires_after_days?: number | null;
  hours_bank_expiration_terms?: string | null;
  expires_at?: string | null;
}

const props = defineProps<{
  quote: QuoteStripQuote;
  compact?: boolean;
}>();

const isHourly = computed(() => props.quote.interval === "hourly");
const isHoursBank = computed(() => props.quote.interval === "hours_bank");
const hasMultipleUnits = computed(() => props.quote.quantity > 1);

const rate = computed(() => formatCurrency(props.quote.amount_cents, props.quote.currency));

const rateLabel = computed(() => {
  if (isHourly.value) return "Hourly rate";
  if (isHoursBank.value) return quoteCopy.rateLabels.hours_bank;
  return hasMultipleUnits.value ? "Unit price" : "Fixed price";
});

const quantity = computed(() => {
  const unit =
    props.quote.interval === "one_time"
      ? quoteCopy.quantityLabels.one_time.unit
      : quoteCopy.quantityLabels.hourly.unit;
  return `${props.quote.quantity} ${unit}${props.quote.quantity !== 1 ? "s" : ""}`;
});

const quantityLabel = computed(() => quoteCopy.quantityLabels[props.quote.interval].stripLabel);

const total = computed(() => {
  const t = props.quote.amount_cents * props.quote.quantity;
  return formatCurrency(t, props.quote.currency);
});

const totalLabel = computed(() => quoteCopy.totalLabels[props.quote.interval]);

const hoursBankExpiration = computed(() => {
  if (!isHoursBank.value || props.quote.hours_bank_expires_after_days == null) return null;
  return `${props.quote.hours_bank_expires_after_days} day${props.quote.hours_bank_expires_after_days !== 1 ? "s" : ""}`;
});

const deliveryWindowTooltip = computed(() => {
  const days = props.quote.delivery_after_days;
  if (days == null) return null;
  return getDeliveryWindowLabel(days, props.quote.includes_weekends ?? false);
});

const deadline = computed(() => {
  const days = props.quote.delivery_after_days;
  if (days != null) {
    const deadlineDate = calculateDeliveryDeadline(
      new Date(),
      days,
      props.quote.includes_weekends ?? false,
    );
    return formatCompactDate(deadlineDate.toISOString());
  }

  return props.quote.expires_at ? formatCompactDate(props.quote.expires_at) : null;
});
</script>

<template>
  <div
    class="flex flex-wrap items-baseline gap-x-6 gap-y-2"
    :class="compact ? 'text-h5' : ''"
  >
    <div class="flex flex-col gap-0.5">
      <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ rateLabel }}</span>
      <UiText as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900 font-semibold">
        {{ rate }}
      </UiText>
    </div>

    <div v-if="quantity && (isHourly || isHoursBank || hasMultipleUnits)" class="flex flex-col gap-0.5">
      <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ quantityLabel }}</span>
      <UiText as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900">
        {{ quantity }}
      </UiText>
    </div>

    <div v-if="deadline" class="flex flex-col gap-0.5">
      <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">Deadline</span>
      <UiTooltip v-if="deliveryWindowTooltip" :content="deliveryWindowTooltip">
        <span class="inline-flex cursor-help">
          <UiText as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900 underline decoration-gray-300 underline-offset-3 decoration-dotted">
            {{ deadline }}
          </UiText>
        </span>
      </UiTooltip>
      <UiText v-else as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900">
        {{ deadline }}
      </UiText>
    </div>

    <div v-if="isHourly || isHoursBank || hasMultipleUnits" class="flex flex-col gap-0.5">
      <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ totalLabel }}</span>
      <UiText as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900 font-bold">
        {{ total }}
      </UiText>
    </div>

    <div v-if="hoursBankExpiration" class="flex flex-col gap-0.5">
      <span class="text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ quoteCopy.hoursBankExpirationDisplayLabel }}</span>
      <UiText as="span" :variant="compact ? 'p-small' : 'p'" class="text-gray-900">
        {{ hoursBankExpiration }}
      </UiText>
    </div>
  </div>
</template>
